/**
 * ConfirmModal — the shared confirm/alert dialog (see DESIGN-SYSTEM.md "Modals").
 */
import type { ReactNode } from "react";
import { useDismissable } from "../../lib/use-dismissable";
import { useModalScrollLock } from "../../lib/use-modal-scroll-lock";
import { Banner } from "./Banner";
import { Button } from "./Button";
import styles from "./ConfirmModal.module.css";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: ReactNode;
  warning?: string;
  /** Failure from the confirm action itself. Rendered inside the modal so the
   *  error appears at the interaction point — callers keep the modal open on
   *  failure instead of routing the message to a page-level banner. */
  error?: string | null;
  confirmLabel?: string;
  confirmVariant?: "primary" | "danger";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  message,
  warning,
  error,
  confirmLabel = "Confirm",
  confirmVariant = "danger",
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  // DESIGN-SYSTEM.md Modals: background scroll locks (counter-guarded — this dialog
  // may layer over a form modal) and Escape cancels, like the backdrop click.
  // The dismissal stack means Escape closes only this, the topmost surface.
  useModalScrollLock(open);
  useDismissable({ open, onDismiss: onCancel, pointer: "none" });

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.title}>{title}</h3>
        <div className={styles.message}>{message}</div>
        {warning && <p className={styles.warning}>{warning}</p>}
        {error && (
          <Banner variant="error" className={styles.errorBanner}>
            {error}
          </Banner>
        )}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
